import { Matrix4 } from "@eplan/wgpurenderer/src/renderer.js";
import { CustomBox3D } from "./CustomBox3D";
import {
  loadEmaCadModel,
  prepareHighlight,
  getNodeBounds,
} from "./EmaCadLoader";
import { hexToRGBA, getColorByTag, createMaterial } from "../Utils/colorUtils";
import { isDummyDuct } from "../../../../builders/dummyDucts";

// The band separator duct gets its width from PlacementOptions, not from the
// scenario's section duct - it is never a placeholder.
const BAND_SEPARATOR_NAME = "Band Cable Duct (vertical separator)";

// ── Rotation on vertical rails ──────────────────────────────────────────
// The engine sends device.rotation = 90 for devices on a vertical rail, and
// width/height already swapped (= the footprint on the plate). Boxes just
// use the footprint; CAD models have to be turned around the axis that is
// perpendicular to the mounting plate (Z here: Y is up, devices stand out
// in +Z).
//
// +1 = counter-clockwise seen from the front: the device's top edge points
// LEFT. This matches the engine, which puts the device's top spacing on the
// left side of the rail. Use -1 if your devices should face the other way.
const VERTICAL_RAIL_TURN = +1;

// Logs CAD models whose size differs from the article size (see below)
const DEBUG_CAD_SIZES = false;

// Places a model: its bbox centre onto `target`, turned by `degrees` around
// Z (perpendicular to the plate) about that centre. Equivalent to
// T(target) * Rz(degrees) * T(-centre), written out directly so it does not
// depend on the multiplication order of Matrix4.multiply.
// Column-major, like the rest of the matrices here (see transformPoint in
// EmaCadLoader: x' = m[0]*x + m[4]*y + m[8]*z + m[12]).
//
// localShiftX: extra shift along the MODEL's own X axis (before turning).
// It is turned together with the model, so a correction that is right for
// the upright model stays right when the model lies on a vertical rail.
export function placeTurned(center, target, degrees, localShiftX = 0) {
  const r = (degrees * Math.PI) / 180;
  const c = Math.round(Math.cos(r) * 1e12) / 1e12; // exact 0 / ±1 for 90° steps
  const s = Math.round(Math.sin(r) * 1e12) / 1e12;
  const tx = target.x + c * localShiftX;
  const ty = target.y + s * localShiftX;
  return [
    c, s, 0, 0,
    -s, c, 0, 0,
    0, 0, 1, 0,
    tx - (c * center.x - s * center.y),
    ty - (s * center.x + c * center.y),
    target.z - center.z,
    1,
  ];
}

// 0 / 90 / 180 / 270 as sent by the engine (anything else is treated as 0)
const normalizedRotation = (value) => {
  const r = Number(value) || 0;
  return ((Math.round(r / 90) * 90) % 360 + 360) % 360;
};

export async function createSections3D({
  renderer,
  allocator,
  storeGeometry,
  materials,
  texture,
  snapshot,
  parent,
  sectionPlacements = [],
  sections = [],
  showSectionBorders = false,
  devices = [],
  colorMap = new Map(),
  selectedProject,
  deviceCadUrls = new Map(),
  insideClearance,
  deviceNodeMap,
}) {
  const { cabinet, ductTopEnclosure, ductLeft, ductRight, generatedDevices } =
    snapshot;
  const width = cabinet?.size?.width ?? 0;
  const height = cabinet?.size?.height ?? 0;
  const depth = cabinet?.size?.depth ?? 600;
  const root = renderer.createInternalNode();
  const montage = snapshot?.montagePlate;
  const resolvedInsideClearance =
    insideClearance ?? cabinet?.insideClearance ?? 0;
  const backPanelWidth = (depth - resolvedInsideClearance) / 2;
  const montageDepth = montage.mountingDistance + backPanelWidth;
  root.name = "Sections";

  const sectionNameBySectionId = new Map();
  for (const s of sections) {
    sectionNameBySectionId.set(s.id, s.name ?? s.id);
  }
  const sectionNameByPlacementId = new Map();

  for (const sp of sectionPlacements) {
    const name =
      sectionNameBySectionId.get(sp.referenceId) ?? `Section ${sp.id}`;
    sectionNameByPlacementId.set(sp.id, name);
  }

  const masterDevById = new Map();
  for (const dv of devices || []) {
    masterDevById.set(dv.id, dv);
  }

  // Section placements whose duct is a placeholder (no duct selected)
  const dummyDuctPlacementIds = new Set(
    sectionPlacements.filter((sp) => isDummyDuct(sp.ductTop)).map((sp) => sp.id),
  );

  for (const section of snapshot.generatedSections) {
    const sectionNode = renderer.createInternalNode();

    const sectionName =
      sectionNameByPlacementId.get(section.sectionPlacementId) ??
      `Section ${section.id}`;

    sectionNode.name = sectionName;

    const sectionY3D = height - section.y - section.height;

    sectionNode.matrix = Matrix4.translation([section.x, sectionY3D, 0]);
    root.addChild(sectionNode);

    for (const device of section.generatedDevices || []) {
      const isDuct = device.deviceTag === "DUCT";
      const isRail = device.deviceTag === "RAIL";
      const isDummyDuctDevice =
        isDuct &&
        device.name !== BAND_SEPARATOR_NAME &&
        dummyDuctPlacementIds.has(section.sectionPlacementId);

      const master = device.deviceId
        ? masterDevById.get(device.deviceId)
        : null;

      let deviceName = [master?.subGroup?.name, master?.subGroup?.articleNumber]
        .filter(Boolean)
        .join(" - ");

      if (isDuct) {
        deviceName = isDummyDuctDevice ? "Duct (placeholder)" : "Duct";
      }

      let color = "#95a5a6";
      if (device.isPlaceholder) {
        color = "#cccccc";
      } else if (isDuct) {
        color = isDummyDuctDevice ? "#B7D3D4" : "#5C9495";
      } else if (!isRail) {
        color = getColorByTag(device.deviceTag);
      }

      if (isRail) continue;

      // Placeholder ducts stay opaque (see createCabinet3D: transparent objects
      // behind the transparent cabinet CAD are not rendered)
      const alpha = device.isPlaceholder ? 0.3 : 1;
      // `await` matters: if createMaterial is async, the un-awaited Promise
      // makes every CustomBox3D (ducts, CAD-less devices) invisible.
      const deviceMaterial = await createMaterial(renderer, allocator, color, alpha);
      const depthFallback = isRail ? 10 : 50;

      let x = device.position.x - section.x;
      let y =
        section.height - (device.position.y - section.y) - device.height / 2;
      let z = montageDepth;

      const subGroupId = master?.subGroup?.id;
      const cadUrl = subGroupId ? deviceCadUrls.get(subGroupId) : null;
      let placed = false;

      if (cadUrl && !device.isPlaceholder) {
        try {
          const boxDepth = device.depth || depthFallback;

          const { node: deviceNode, referencePoint } = await loadEmaCadModel(
            renderer,
            allocator,
            cadUrl,
          );
          deviceNode.name = deviceName;
          deviceNode.matrix = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
          await prepareHighlight(
            deviceNode,
            renderer,
            storeGeometry,
            texture,
            materials[6],
          );
          // Measure bounds against whatever matrix the loader already set
          const bounds = getNodeBounds(deviceNode, renderer, { debug: false });

          const bboxCenter = {
            x: (bounds.minX + bounds.maxX) / 2,
            y: (bounds.minY + bounds.maxY) / 2,
            z: (bounds.minZ + bounds.maxZ) / 2,
          };
          // Centre of the footprint (width/height already swapped by the
          // engine for rotated devices) - same formula as the CustomBox3D path
          const targetCenter = {
            x: device.position.x - section.x + device.width / 2,
            y:
              section.height -
              (device.position.y - section.y) -
              device.height +
              device.height / 2,
            z: montageDepth + boxDepth / 2,
          };

          const rotation = normalizedRotation(device.rotation);
          const turned = rotation === 90 || rotation === 270;

          // The CAD bbox is often wider than the device body (extra variant /
          // clip geometry on one side). The existing correction moves the
          // model by half that excess along its own X axis so the BODY sits
          // on the footprint. Same for turned devices - measured against the
          // body's own width (= footprint height when turned) and turned
          // together with the model.
          const cadWidth = bounds.maxX - bounds.minX;
          const bodyWidth = turned ? device.height : device.width;
          const localShiftX = -(cadWidth - bodyWidth) / 2;

          // bbox centre -> footprint centre, turned around Z for vertical
          // rails. The bounds were measured with the identity matrix set
          // above, so this matrix replaces it completely.
          deviceNode.matrix = placeTurned(
            bboxCenter,
            targetCenter,
            rotation * VERTICAL_RAIL_TURN,
            localShiftX,
          );

          // DEBUG: models whose bbox differs a lot from the article size are
          // the usual reason for overlaps with ducts.
          if (DEBUG_CAD_SIZES) {
            const cadHeight = bounds.maxY - bounds.minY;
            const bodyHeight = turned ? device.width : device.height;
            if (Math.abs(cadWidth - bodyWidth) > 1 || Math.abs(cadHeight - bodyHeight) > 1) {
              console.log("[3D CAD size]", deviceName, {
                rotation,
                article: { width: bodyWidth, height: bodyHeight },
                cad: { width: +cadWidth.toFixed(1), height: +cadHeight.toFixed(1) },
              });
            }
          }

          sectionNode.addChild(deviceNode);

          const key = master?.subGroup?.id;

          if (key) {
            const nodes = deviceNodeMap.get(key) ?? [];
            nodes.push(deviceNode);
            deviceNodeMap.set(key, nodes);
          }
          placed = true;
        } catch (err) {
          console.error(`Failed loading CAD for device ${deviceName}:`, err);
        }
      }

      if (!placed) {
        // Boxes need no rotation: width/height are the footprint already
        const boxDepth = device.depth || 40;
        const deviceNode = await CustomBox3D({
          renderer,
          allocator,
          storeGeometry,
          parent: sectionNode,
          material: deviceMaterial,
          texture,
          x,
          y,
          z,
          width: device.width,
          height: device.height,
          depth: device.depth || depthFallback,
          nodeName: deviceName,
        });

        deviceNode.matrix = Matrix4.product([
          Matrix4.translation([
            device.position.x - section.x + device.width / 2,

            section.height -
              (device.position.y - section.y) -
              device.height +
              device.height / 2,

            montageDepth + boxDepth / 2,
          ]),
          Matrix4.scale([device.width / 2, device.height / 2, boxDepth / 2]),
        ]);
        // (no addChild here - CustomBox3D already attached it to sectionNode)
        const key = master?.subGroup?.id;

        if (key) {
          const nodes = deviceNodeMap.get(key) ?? [];
          nodes.push(deviceNode);
          deviceNodeMap.set(key, nodes);
        }
      }
    }
  }

  parent.addChild(root);
  return root;
}